import React, { useState, useEffect } from 'react'
import { MenuItem, Position, Checkbox } from '@blueprintjs/core'
import { Select2, ItemListPredicate } from '@blueprintjs/select'
import { Button, IconButton } from '..'
import { IUIButtonProps } from '../buttons/Button'
import fuzzy from 'fuzzaldrin-plus'
import ClearableSelectWrapper from './ClearableSelectWrapper'
import { Flex, HighlightedText } from '@ui'
import api from '@api'

interface EmployeeTypeSelectProps {
  value: string | null
  clearable?: boolean
  onChange: (employeeType: string | null) => void
  buttonProps?: IUIButtonProps
  btnText?: string
  itemDisabled?: (item: string) => boolean
  multi?: boolean
}

const EmployeeTypeSelect = Select2<string>

export default function EmployeeTypeSelectWrapper({
  value,
  clearable,
  onChange,
  buttonProps,
  btnText,
  itemDisabled,
  multi,
}: EmployeeTypeSelectProps) {
  const employeeTypes: string[] = useEmployeeTypes()

  return (
    <ClearableSelectWrapper>
      <EmployeeTypeSelect
        resetOnSelect
        popoverProps={{
          position: Position.BOTTOM,
          modifiers: { arrow: { enabled: false } },
          minimal: true,
        }}
        noResults={<MenuItem disabled text="No results" />}
        items={employeeTypes}
        itemRenderer={(employeeType, { handleClick, modifiers, query }) => {
          return modifiers.matchesPredicate ? (
            <MenuItem
              data-item={employeeType}
              active={modifiers.active}
              key={employeeType}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                if (multi) {
                  e.preventDefault()
                  onChange(employeeType)
                  return
                }
                handleClick(e)
              }}
              text={
                <Flex>
                  {multi && <Checkbox checked={itemDisabled && itemDisabled(employeeType)} />}
                  <HighlightedText text={employeeType} query={query} />
                </Flex>
              }
            />
          ) : null
        }}
        itemListPredicate={filterEmployeeTypes}
        onItemSelect={onChange}
      >
        <Button
          {...buttonProps}
          rightIconName={value ? undefined : btnText ? undefined : 'chevron-down'}
          text={value ? value : btnText || 'Select'}
        />
      </EmployeeTypeSelect>
      {value && clearable && (
        <IconButton
          minimal
          iconName="times"
          data-button="clear-button"
          tooltip="Clear"
          onClick={() => onChange(null)}
        />
      )}
    </ClearableSelectWrapper>
  )
}

const filterEmployeeTypes: ItemListPredicate<string> = (query, employeeTypes) => {
  return query ? fuzzy.filter(employeeTypes, query) : employeeTypes
}

let persistedState: string[] = []

export function useEmployeeTypes(): string[] {
  const [employeeTypes, setEmployeeTypes] = useState<string[]>(persistedState)

  useEffect(() => {
    api.company.employeeTypes().then((employeeTypes) => setEmployeeTypes(employeeTypes))
  }, [])

  useEffect(() => {
    persistedState = employeeTypes
  })

  return employeeTypes
}
